import { HelpCatalogue, HelpLang, HelpSectionEntry, LocalText } from '../types';

/** The text fields of an entry that can be translated (keys, links and flags stay as they are). */
export type TranslatableField = 'title' | 'what' | 'business' | 'next' | 'connects';

/**
 * One translated file, e.g. help/i18n/bn/formsSales.ts. It lists only the keys and fields that are
 * translated; anything left out keeps showing English. The English files are never edited.
 */
export interface HelpTranslation {
    elements?: Record<string, Partial<Record<TranslatableField, string>>>;
    sections?: Record<string, Partial<Record<TranslatableField, string>>>;
}

export const defineTranslation = (t: HelpTranslation): HelpTranslation => t;

export type HelpTranslationLoader = () => Promise<{ default: HelpTranslation }>;

/**
 * Per language, one loader per translated file. Empty: nothing is translated yet.
 * Example once Bangla exists: `bn: { formsSales: () => import('./bn/formsSales') }`.
 */
export const HELP_TRANSLATION_LOADERS: Partial<Record<HelpLang, Record<string, HelpTranslationLoader>>> = {};

const FIELDS: TranslatableField[] = ['title', 'what', 'business', 'next', 'connects'];

/**
 * Lays translations over the English catalogue as `{ en, [lang] }` texts. Returns a new catalogue
 * plus the translated keys that no longer exist in English (stale translations to clean up).
 */
export function applyTranslations(catalogue: HelpCatalogue, translations: HelpTranslation[], lang: HelpLang): { catalogue: HelpCatalogue; unknownKeys: string[] } {
    const out: HelpCatalogue = { elements: { ...catalogue.elements }, sections: { ...catalogue.sections } };
    const unknownKeys: string[] = [];
    for (const tr of translations) {
        for (const kind of ['elements', 'sections'] as const) {
            for (const [key, fields] of Object.entries(tr[kind] ?? {})) {
                const base = out[kind][key] as HelpSectionEntry | undefined;
                if (!base) { unknownKeys.push(`${kind}:${key}`); continue; }
                const next: HelpSectionEntry = { ...base };
                for (const f of FIELDS) {
                    const text = fields[f];
                    const en: LocalText | undefined = base[f];
                    if (text && en) next[f] = { ...en, [lang]: text };
                }
                (out[kind] as Record<string, HelpSectionEntry>)[key] = next;
            }
        }
    }
    return { catalogue: out, unknownKeys };
}
